import React, { useEffect, useState, useCallback } from 'react';
import BreakCrumbs from '../../components/BreakCrumbs';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Button,
  Paper,
  Typography,
  Divider,
  TextField,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { trim, debounce } from 'lodash';
import Swal from 'sweetalert2';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import KeyboardReturnOutlinedIcon from '@material-ui/icons/KeyboardReturnOutlined';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import RestoreIcon from '@material-ui/icons/Restore';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { alertError } from '../../Util/Util';
import { MemberTypeList } from '../../data/common';
import storeAPI from '../../middleware/storeAPI';
import memberAPI from '../../middleware/memberAPI';
import PayingMemberBenefitsEditor from '../../components/Stores/PayingMemberBenefitsEditor';
import Autocomplete from '@material-ui/lab/Autocomplete';

// 過濾掉空白或只有空白字符的項目（供父組件在儲存時使用）
const filterEmptyBenefits = (benefitsList) => {
  return benefitsList.filter((benefit) => benefit && benefit.trim() !== '');
};

const useStyles = makeStyles((theme) => ({
  uploadContainer: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  uploadContainerActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  previewContainer: {
    position: 'relative',
    display: 'inline-block',
    marginTop: theme.spacing(2),
  },
  previewImage: {
    width: 200,
    height: 200,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  restoreButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  uploadText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  uploadHint: {
    color: theme.palette.text.disabled,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
}));

const Store = (props) => {
  const { match } = props;
  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreate, setIsCreate] = useState(() => {
    return trim(id) === '';
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    payingMemberBenefits: [],
    staffUserId: '',
    imageFile: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [memberList, setMemberList] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);

  // Helper function: 轉換會員資料為統一格式
  const transformMemberData = (member) => {
    if (!member) return null;

    return {
      id: member.id,
      realName: member.realName || '',
      cellPhone: member.cellPhone || '',
      email: member.email || '',
    };
  };

  useEffect(() => {
    const checkIsCreateRs = trim(id) === '';
    setIsCreate(checkIsCreateRs);

    if (!checkIsCreateRs) {
      getStore();
    }
  }, [id]);

  const getStore = () => {
    setIsLoading(true);

    return storeAPI
      .getStore(id)
      .then(
        (store) => {
          if (store) {
            setForm({
              id: store.id,
              name: store.name,
              payingMemberBenefits: store.payingMemberBenefits || [],
              staffUserId: store.staffUserInfo ? store.staffUserInfo.id : '',
              imageFile: null,
            });
            // 保存原始圖片 URL
            const originalImg = store.imgSrc || null;
            setImagePreview(originalImg);
            setOriginalImageUrl(originalImg);
            // 設定選中的員工（統一格式）
            if (store.staffUserInfo) {
              setSelectedMember(transformMemberData(store.staffUserInfo));
            }
          }
        },
        (error) => {
          alertError(error);
        },
      )
      .then(() => {
        setIsLoading(false);
      });
  };

  const loadMembers = (keyword) => {
    setMemberLoading(true);
    memberAPI
      .getMemberList(1, 50, MemberTypeList.restaurantStaff, keyword)
      .then(
        (rs) => {
          // 只保留需要的欄位
          const simplifiedMembers = (rs.memberList || []).map(
            transformMemberData,
          );
          setMemberList(simplifiedMembers);
        },
        (error) => {
          console.error('Failed to load members:', error);
        },
      )
      .then(() => {
        setMemberLoading(false);
      });
  };

  const debouncedLoadMembers = useCallback(
    debounce((keyword) => {
      loadMembers(keyword);
    }, 500),
    [],
  );

  const handleChangeForm = (name, value) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 驗證檔案類型
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          title: '請上傳圖片檔案',
          icon: 'warning',
        });
        return;
      }

      // 驗證檔案大小 (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          title: '圖片大小不能超過 5MB',
          icon: 'warning',
        });
        return;
      }

      setForm({
        ...form,
        imageFile: file,
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRestoreImage = () => {
    // 還原到原始圖片
    setForm({
      ...form,
      imageFile: null,
    });
    setImagePreview(originalImageUrl);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      // 模擬 file input change event
      handleImageChange({ target: { files: [file] } });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSubmit = () => {
    if (trim(form.name) === '') {
      Swal.fire({
        title: '請輸入分店名稱',
        icon: 'info',
      });
      return false;
    }

    setIsLoading(true);

    const submitData = {
      id: form.id,
      name: form.name,
      payingMemberBenefits: JSON.stringify(
        filterEmptyBenefits(form.payingMemberBenefits),
      ),
      staffUserId: form.staffUserId,
      imageFile: form.imageFile,
    };

    storeAPI.createOrUpdateStore(submitData).then(
      (rs) => {
        Swal.fire({
          title: '儲存完成',
          icon: 'success',
        });
        history.push('/Stores/StoreList');
      },
      (error) => {
        alertError(error);
        setIsLoading(false);
      },
    );
  };

  return (
    <div className="popular pagefadeIn">
      <BreakCrumbs path={match.url}></BreakCrumbs>

      <Box component={Paper} square elevation={2} p={1} mt={3} mb={3}>
        <Typography variant="h5" color="textPrimary" gutterBottom>
          {isCreate ? '建立分店' : '修改分店'}
        </Typography>

        <Divider light />

        <Box p={3}>
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="flex-start"
            spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth={true}
                label="分店名稱"
                value={form.name || ''}
                onChange={(e) => {
                  handleChangeForm('name', e.target.value);
                }}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={memberList}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const name = option.realName || '';
                  const phone = option.cellPhone || '';
                  const email = option.email || '';

                  if (name && email) return `${name} (${email})`;
                  if (name && phone) return `${name} (${phone})`;
                  if (email) return email;
                  if (name) return name;
                  return `ID: ${option.id}`;
                }}
                getOptionSelected={(option, value) => {
                  return option.id === value.id;
                }}
                value={selectedMember}
                loading={memberLoading}
                disabled={isLoading}
                onChange={(event, newValue) => {
                  setSelectedMember(newValue);
                  handleChangeForm('staffUserId', newValue ? newValue.id : '');
                }}
                onInputChange={(event, newInputValue) => {
                  debouncedLoadMembers(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="負責員工"
                    placeholder="輸入姓名、手機搜尋..."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                分店圖片
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="store-image-upload"
                type="file"
                onChange={handleImageChange}
                disabled={isLoading}
              />
              {!imagePreview ? (
                <label htmlFor="store-image-upload">
                  <Box
                    className={classes.uploadContainer}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}>
                    <CloudUploadIcon className={classes.uploadIcon} />
                    <Typography variant="body1" className={classes.uploadText}>
                      點擊或拖曳圖片至此區域上傳
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.uploadHint}>
                      支援 JPG、PNG 格式，檔案大小不超過 5MB
                    </Typography>
                  </Box>
                </label>
              ) : (
                <Box className={classes.previewContainer}>
                  <img
                    src={imagePreview}
                    alt="分店圖片預覽"
                    className={classes.previewImage}
                  />
                  {originalImageUrl && imagePreview !== originalImageUrl && (
                    <IconButton
                      size="small"
                      className={classes.restoreButton}
                      onClick={handleRestoreImage}
                      disabled={isLoading}
                      title="還原到原始圖片">
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Box mt={2} textAlign="center">
                    <label htmlFor="store-image-upload">
                      <Button
                        variant="outlined"
                        color="primary"
                        component="span"
                        startIcon={<PhotoCameraIcon />}
                        disabled={isLoading}>
                        更換圖片
                      </Button>
                    </label>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <PayingMemberBenefitsEditor
                benefits={form.payingMemberBenefits}
                onChange={(newBenefits) => {
                  handleChangeForm('payingMemberBenefits', newBenefits);
                }}
                disabled={isLoading}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider light />

        <Box p={1} mt={2} mb={2} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            size="large"
            style={{ marginRight: '24px' }}
            startIcon={<SaveOutlinedIcon />}
            disabled={isLoading}
            onClick={handleSubmit}>
            {isCreate ? '建立' : '修改'}
          </Button>
          <Button
            variant="contained"
            color="default"
            size="large"
            startIcon={<KeyboardReturnOutlinedIcon />}
            disabled={isLoading}
            onClick={() => {
              history.push('/Stores/StoreList');
            }}>
            回列表
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default Store;
